import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';

import { Vibration } from 'react-native';
import { RootState } from '../../store/reducers';
import { payoffActions } from '../../store/reducers/payoff';
import { reportActions } from '../../store/reducers/report';
import { scheduleSitesThunk } from '../../store/reducers/schedulesites';
import * as reportcacheThunks from '../../store/reducers/reportcache/reportcache.actionThunk';
import { reportsubmitActions } from '../../store/reducers/reportsubmit';
import { tutorialThunks } from '../../store/reducers/tutorial';
import { FlagCount, Report, ReportSection } from '../../utils/classes';
import { calculateTimeDuration } from '../../utils/date';
import { getCurrentUserIdentity } from '../../utils/userIdentity';

import { PayoffPageProps } from '../../routes/payoff';
import getReportScore from '../submitreport/utils/getReportScore';
import PayOffContainer from './PayoffContainer';
import { npsTriggerAsync, npsResetSession, npsOpenModal } from '../../store/reducers/nps/nps.action';
import { clearNpsCompletionCallback, setNpsCompletionCallback } from '../nps/npsCallback';

import { selectProfile } from '../../store/selectors/profile';
const POST_UPLOAD_SCHEDULE_REFRESH_DELAYS_MS = [0, 2000, 5000, 10000, 14000];
const POST_UPLOAD_CACHE_CLEANUP_DELAY_MS = 15000;

/**
 * Payoff Screen shows up after the report has been succesfully uploaded to the server.
 *
 * It will show the recap of the report such as number of each flags, the score, date of visit, time visit
 * and the time duration of each report
 */
export class Payoff extends React.Component<PayoffProps, PayoffState> {
  private hasTriggeredLiteNps = false;

  state: PayoffState = {
    /** Number of flag for each color (red, yellow, green) */
    flagCount: null,
    /** Score result for (in integer) */
    scoreRaw: null,
    /** Score result for (in integer) */
    scoreTotal: null,
    /** Score result for (in percentage) */
    scoreWeighted: null,
    /** Date of auditor visit */
    dateVisit: null,
    /** Time of auditor visit */
    timeVisit: null,
    /** How long is the audit going on */
    timeDuration: null,
    /** Track whether the user has already pressed Got It while lite NPS is still resolving */
    isAwaitingLiteNpsDecision: false
  };

  // Count total number of flag / score based on the completed report
  constructor(props: PayoffProps) {
    super(props);
    const { uploadingReport, preGeneratedReport } = props;

    // preGeneratedReport comes from report payload from notification
    const selectedReport = preGeneratedReport ? preGeneratedReport : uploadingReport;

    const dateVisit = selectedReport
      ? moment.parseZone(selectedReport.datetimeIn).format('DD MMMM YYYY')
      : moment().format('DD MMMM YYYY');
    const timeVisit = selectedReport
      ? moment.parseZone(selectedReport.datetimeIn).format('HH:mm A')
      : moment().format('HH:mm A');
    const timeDuration = selectedReport
      ? calculateTimeDuration(selectedReport.datetimeIn, selectedReport.datetimeOut)
      : '00:00:00';

    const { flagCount, scoreWeighted, scoreRaw, scoreTotal } = getReportScore(selectedReport as Report);

    this.state = {
      flagCount,
      scoreRaw,
      scoreTotal,
      scoreWeighted,
      dateVisit,
      timeVisit,
      timeDuration,
      isAwaitingLiteNpsDecision: false
    };
  }

  // Vibrate for celebration
  componentDidMount() {
    const { uploadingReportKey, uploadingSiteKey, uploadingReport, selectedSite, preGeneratedReport, isUploading } =
      this.props;
    // On Payoff screen accessed from notification message
    // ensures action does not get called when an upload is in progress
    if (!isUploading && !preGeneratedReport && uploadingReport) {
      this.props.selectReport(null, null);
      this.props.resetReportToUpload();

      if (selectedSite && selectedSite.isMultiSite) {
        this.props.removePendingMulti(uploadingSiteKey!, uploadingReportKey!);
      } else {
        this.props.removePendingSingle(uploadingSiteKey!, uploadingReport.scheduleKey!, uploadingReportKey!);
      }

      this.queuePostUploadScheduleSync();
    }

    if (preGeneratedReport) {
      this.props.resetPreGeneratedReport();
    }

    Vibration.vibrate(500, false);

    this.maybeTriggerLiteNps();
  }

  componentDidUpdate() {
    this.maybeTriggerLiteNps();
    this.maybeContinueLiteNpsFlow();
  }

  private maybeTriggerLiteNps = () => {
    const { auth, profile, npsStartDateTime, npsTriggerSource } = this.props;
    const { userId, organizationId } = getCurrentUserIdentity(profile, auth);

    if (this.hasTriggeredLiteNps || npsStartDateTime || npsTriggerSource || !userId || !organizationId) {
      return;
    }

    this.hasTriggeredLiteNps = true;
    this.props.resetNpsSession();
    this.props.triggerNps({
      respondentId: userId,
      organizationID: organizationId,
      triggerSource: 'Report Submission (Single Audit)',
      startDateTime: new Date().toISOString()
    });
  };

  private queuePostUploadScheduleSync = () => {
    const { organization, uploadingSiteKey, uploadingReportKey } = this.props;

    POST_UPLOAD_SCHEDULE_REFRESH_DELAYS_MS.forEach(delay => {
      setTimeout(() => {
        if (organization?.schedule) {
          this.props.getMyScheduledSites(organization.schedule);
        }
      }, delay);
    });

    setTimeout(() => {
      this.props.removeReport(uploadingSiteKey!, uploadingReportKey!, true);
    }, POST_UPLOAD_CACHE_CLEANUP_DELAY_MS);
  };

  private maybeContinueLiteNpsFlow = () => {
    const {
      npsIsEligible,
      npsIsModalOpen,
      npsQuestionnaire,
      npsIsTriggering,
      npsIsQuestionnaireLoading,
      npsQuestionnaireError
    } = this.props;

    if (!this.state.isAwaitingLiteNpsDecision) {
      return;
    }

    if (npsIsEligible && npsQuestionnaire && !npsIsModalOpen) {
      this.setState({ isAwaitingLiteNpsDecision: false }, () => {
        this.props.openNpsModal();
      });
      return;
    }

    if (!npsIsTriggering && !npsIsQuestionnaireLoading) {
      this.setState({ isAwaitingLiteNpsDecision: false }, () => {
        if (!npsIsEligible || npsQuestionnaireError) {
          clearNpsCompletionCallback();
          this.doNavHome();
        }
      });
    }
  };

  doNavHome = () => {
    const { isTutorialMode, navigation, route } = this.props;
    if (isTutorialMode) {
      this.props.dismissTutorialMode();
    }

    return navigation.reset({
      index: 0,
      routes: [{ name: route.params.originTab || 'HomeTab' }]
    });
  };

  handleNavHome = () => {
    if (this.props.npsIsEligible || this.props.npsIsTriggering || this.props.npsIsQuestionnaireLoading) {
      // Align with audit-lite: trigger/check runs in the background on payoff mount,
      // but the NPS modal is only surfaced after the user presses Got It.
      setNpsCompletionCallback(this.doNavHome);
      this.setState({ isAwaitingLiteNpsDecision: true });
      return;
    }
    this.doNavHome();
  };

  render() {
    return (
      <PayOffContainer
        language={this.props.language}
        selectedSite={this.props.selectedSite!}
        flagCount={this.state.flagCount}
        scoreRaw={this.state.scoreRaw}
        scoreTotal={this.state.scoreTotal}
        scoreWeighted={this.state.scoreWeighted}
        dateVisit={this.state.dateVisit}
        timeVisit={this.state.timeVisit}
        timeDuration={this.state.timeDuration}
        isCompletingNps={
          this.state.isAwaitingLiteNpsDecision && (this.props.npsIsTriggering || this.props.npsIsQuestionnaireLoading)
        }
        handleNavHome={this.handleNavHome}
      />
    );
  }
}

type PayoffState = {
  flagCount: null | FlagCount;
  scoreRaw: null | number;
  scoreTotal: null | number;
  scoreWeighted: null | number;
  dateVisit: null | string;
  timeVisit: null | string;
  timeDuration: null | string;
  isAwaitingLiteNpsDecision: boolean;
};

interface WithStateProps extends ReturnType<typeof mapStateToProps> {}
interface WithDispatchProps extends ReturnType<typeof mapDispatchToProps> {}

interface PayoffProps extends PayoffPageProps, WithStateProps, WithDispatchProps {
  children: React.ReactNode;
}

const mapStateToProps = (state: RootState) => ({
  auth: state.firebase.auth,
  profile: selectProfile(state),
  uploadingSiteKey: state.reportsubmit.uploadingSiteKey,
  uploadingReportKey: state.reportsubmit.uploadingReportKey,
  selectedSite: state.site.selectedSite,
  selectedSiteKey: state.site.selectedSiteKey,
  selectedSiteSchedule: state.site.selectedSiteSchedule,
  reports: state.reportcache.reports,
  preGeneratedReport: state.payoff.preGeneratedReport,
  isUploading: state.uploadreport.isUploading,
  uploadingReport: state.reportsubmit.uploadingReport,
  uploadingReportSummary: state.reportsubmit.uploadingReportSummary,
  language: selectProfile(state).language || 'en',
  organization: state.organization.myOrganization,
  isTutorialMode: state.tutorial.isTutorialMode,
  profileColor: state.profiletheme.color,
  npsIsEligible: state.npsUser.isEligible,
  npsIsTriggering: state.npsUser.isTriggering,
  npsIsModalOpen: state.npsUser.isModalOpen,
  npsIsQuestionnaireLoading: state.npsUser.isQuestionnaireLoading,
  npsQuestionnaire: state.npsUser.questionnaire,
  npsQuestionnaireError: state.npsUser.questionnaireError,
  npsStartDateTime: state.npsUser.startDateTime,
  npsTriggerSource: state.npsUser.triggerSource
});

export const mapDispatchToProps = (dispatch: any) => ({
  getMyScheduledSites: (orgSchedule: any) => dispatch(scheduleSitesThunk.getMyScheduledSites(orgSchedule)),
  resetPreGeneratedReport: () => dispatch(payoffActions.resetPreGeneratedReport()),
  /**
   * Select a report, which saves the key of that report
   * @param key - The selected report key
   * @param value - The value of Report Object
   */
  selectReport: (key: string | null, value: Report | ReportSection | null) =>
    dispatch(reportActions.selectReport(key, value)),
  /**
   * Reset the redux state used for upload
   */
  resetReportToUpload: () => dispatch(reportsubmitActions.resetReportToUpload()),
  /**
   * Remove the Pending Multi Site
   * @param siteKey - The Site key
   * @param reportKey - The Selected Report Key
   */
  removePendingMulti: (siteKey: string, reportKey: string) =>
    dispatch(reportsubmitActions.removePendingMulti(siteKey, reportKey)),
  /**
   * Remove the Pending Single Site
   * @param siteKey - The Site Key
   * @param questionnaireKey - The Questionnaire Key
   * @param reportKey - The Selected Report Key
   */
  removePendingSingle: (siteKey: string, questionnaireKey: string, reportKey: string) =>
    dispatch(reportsubmitActions.removePendingSingle(siteKey, questionnaireKey!, reportKey)),
  /**
   * Remove the report from redux state including the cache and summary
   * @param siteKey - The Site Key
   * @param reportKey - The Report Key
   * @param backup - True to save a backup report
   */
  removeReport: (siteKey: string, reportKey: string, backup: boolean) =>
    dispatch(reportcacheThunks.removeReport(siteKey, reportKey, backup)),

  /**
   * dismiss tutorial mode and update database that inform the user already do tutorial
   */
  dismissTutorialMode: () => dispatch(tutorialThunks.dismissTutorialMode()),
  triggerNps: (payload: Parameters<typeof npsTriggerAsync.request>[0]) => dispatch(npsTriggerAsync.request(payload)),
  resetNpsSession: () => dispatch(npsResetSession()),
  openNpsModal: () => dispatch(npsOpenModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(Payoff as any) as any;
